/**
 * 勘定科目別の予実(2026-10-23・Board)。2027年度予算フォーマット v8 の「区分 > 科目① > 科目②」と同じ行の形で、
 * 予算・実績・見込みを並べる(docs/specs/2026-10-全体設計.md 10章)。
 *
 *  - 行の元は Gate の勘定科目マスタ(accounts・sub_accounts)。区分は accounts.item_category(売上/原価/販管/外収/外費/特利/特損)。
 *    item_category が無い科目は pl_line から(revenue→売上・cogs→原価・expense→販管)。pl_line も無い科目(資産・負債)は載せない
 *  - 実績・見込みの振り分けは plEntryOf() だけを使う(状態を直接並べない)。月は計上日の月。資金移動は含めない
 *  - 予算は今のところ科目①単位(budgets)。科目②の予算は未対応(v8 の予算を取り込むときに足す)
 *  - 利益: 粗利 = 売上 − 原価 / 営業利益 = 粗利 − 販管 / 経常利益 = 営業利益 + 外収 − 外費 /
 *          税引前当期純利益 = 経常利益 + 特利 − 特損 / 税金等(概算)= 月ごとに MAX(0, 税引前 × 率) の合計 / 税引後 = 税引前 − 税金等
 *    (v8 の数式と同じ。率は v8「設定・運用ルール」の法人税等 40%・消費税(利益比)10%)
 *  - 5行サマリー(売上/原価/粗利/経費/部門利益)の契約はこの集計では変えない(別の表示)
 *
 * 具体例は tests/unit/accountPl.test.ts
 */
import type { Account, Budget, DateStr, MonthStr, SubAccount, Transaction, Yen } from '@core/lib/types';
import { plEntryOf } from './plRecognition';

export type SectionKey = 'sales' | 'cogs' | 'sga' | 'non_op_income' | 'non_op_expense' | 'extra_income' | 'extra_loss';

export const SECTIONS: Array<{ key: SectionKey; itemCategory: string; label: string; cost: boolean }> = [
  { key: 'sales', itemCategory: '売上', label: 'I. 売上高', cost: false },
  { key: 'cogs', itemCategory: '原価', label: 'II. 売上原価', cost: true },
  { key: 'sga', itemCategory: '販管', label: 'III. 販売費及び一般管理費', cost: true },
  { key: 'non_op_income', itemCategory: '外収', label: 'IV. 営業外収益', cost: false },
  { key: 'non_op_expense', itemCategory: '外費', label: 'V. 営業外費用', cost: true },
  { key: 'extra_income', itemCategory: '特利', label: 'VI. 特別利益', cost: false },
  { key: 'extra_loss', itemCategory: '特損', label: 'VII. 特別損失', cost: true },
];

/** 税金等の概算率(v8 設定・運用ルール)。月ごとに MAX(0, 税引前 × 率) */
export const TAX_ESTIMATE = { corporate: 0.4, consumption: 0.1 } as const;

export function sectionOf(a: Pick<Account, 'item_category' | 'pl_line'>): SectionKey | null {
  const byItem = SECTIONS.find((s) => s.itemCategory === (a.item_category ?? '').trim());
  if (byItem) return byItem.key;
  if (a.pl_line === 'revenue') return 'sales';
  if (a.pl_line === 'cogs') return 'cogs';
  if (a.pl_line === 'expense') return 'sga';
  return null;
}

export type Amounts = { budget: Yen; actual: Yen; forecast: Yen };

const zero = (): Amounts => ({ budget: 0, actual: 0, forecast: 0 });

function add(into: Amounts, from: Amounts): void {
  into.budget += from.budget;
  into.actual += from.actual;
  into.forecast += from.forecast;
}

export type SubRow = {
  /** null = 科目②の指定がない取引(v8 の「科目②未選択」の検算欄にあたる) */
  subAccountId: string | null;
  name: string;
  actual: Yen;
  forecast: Yen;
};

export type AccountRow = { account: Account; amounts: Amounts; subs: SubRow[] };

export type SectionRow = { key: SectionKey; label: string; cost: boolean; accounts: AccountRow[]; total: Amounts };

export type ProfitKey = 'gross' | 'operating' | 'ordinary' | 'pretax' | 'tax' | 'net';

export const PROFIT_LABELS: Record<ProfitKey, string> = {
  gross: '売上総利益(粗利)',
  operating: '営業利益',
  ordinary: '経常利益',
  pretax: '税引前 当期純利益',
  tax: `税金等(概算: 法人税等${TAX_ESTIMATE.corporate * 100}%・消費税${TAX_ESTIMATE.consumption * 100}%)`,
  net: '税引後 当期純利益',
};

export type AccountPl = {
  months: MonthStr[];
  sections: SectionRow[];
  profits: Record<ProfitKey, Amounts>;
  /** 売上高(対売上比の分母) */
  sales: Amounts;
  /** マスタに無い科目の取引の件数(マスタから消えた科目など。画面で注意を出す) */
  unknownAccountCount: number;
};

type MonthTotals = Record<SectionKey, Amounts>;

function profitsOf(t: MonthTotals): Record<Exclude<ProfitKey, 'tax' | 'net'>, Amounts> {
  const calc = (f: (k: keyof Amounts) => number): Amounts => ({ budget: f('budget'), actual: f('actual'), forecast: f('forecast') });
  const gross = calc((k) => t.sales[k] - t.cogs[k]);
  const operating = calc((k) => gross[k] - t.sga[k]);
  const ordinary = calc((k) => operating[k] + t.non_op_income[k] - t.non_op_expense[k]);
  const pretax = calc((k) => ordinary[k] + t.extra_income[k] - t.extra_loss[k]);
  return { gross, operating, ordinary, pretax };
}

function taxOf(pretax: number): number {
  return Math.round(Math.max(0, pretax * TAX_ESTIMATE.corporate) + Math.max(0, pretax * TAX_ESTIMATE.consumption));
}

export function buildAccountPl(params: {
  accounts: Account[];
  subAccounts: SubAccount[];
  transactions: Transaction[];
  budgets: Budget[];
  months: MonthStr[];
  today?: DateStr;
}): AccountPl {
  const { accounts, subAccounts, transactions, budgets, months, today } = params;
  const monthSet = new Set(months);
  const accountById = new Map(accounts.map((a) => [a.id, a]));
  const subById = new Map(subAccounts.map((s) => [s.id, s]));

  // 科目①ごとの金額・科目②ごとの金額・月×区分の合計(税金等は月ごとに計算するため)
  const byAccount = new Map<string, Amounts>();
  const bySub = new Map<string, Map<string | null, { actual: Yen; forecast: Yen }>>();
  const byMonth = new Map<MonthStr, MonthTotals>();
  const monthTotals = (m: MonthStr): MonthTotals => {
    let t = byMonth.get(m);
    if (!t) {
      t = Object.fromEntries(SECTIONS.map((s) => [s.key, zero()])) as MonthTotals;
      byMonth.set(m, t);
    }
    return t;
  };
  for (const m of months) monthTotals(m);
  let unknownAccountCount = 0;

  for (const t of transactions) {
    if (t.type === 'transfer') continue;
    const e = plEntryOf(t, today);
    if (e.category === 'excluded' || !monthSet.has(e.month)) continue;
    const account = accountById.get(t.account_id);
    if (!account) {
      unknownAccountCount++;
      continue;
    }
    const section = sectionOf(account);
    if (!section) continue;
    const field = e.category === 'actual' ? 'actual' : 'forecast';

    const acc = byAccount.get(account.id) ?? zero();
    acc[field] += e.amount;
    byAccount.set(account.id, acc);

    const subKey = t.sub_account_id ? t.sub_account_id : null;
    const subs = bySub.get(account.id) ?? new Map();
    const s = subs.get(subKey) ?? { actual: 0, forecast: 0 };
    s[field] += e.amount;
    subs.set(subKey, s);
    bySub.set(account.id, subs);

    monthTotals(e.month)[section][field] += e.amount;
  }

  for (const b of budgets) {
    if (!monthSet.has(b.month)) continue;
    const account = accountById.get(b.account_id);
    const section = account ? sectionOf(account) : null;
    if (!account || !section) continue;
    const acc = byAccount.get(account.id) ?? zero();
    acc.budget += b.amount;
    byAccount.set(account.id, acc);
    monthTotals(b.month)[section].budget += b.amount;
  }

  const sections: SectionRow[] = SECTIONS.map((def) => {
    const rows = accounts
      .filter((a) => sectionOf(a) === def.key && (a.active || byAccount.has(a.id)))
      .sort((a, b) => a.display_order - b.display_order || a.name.localeCompare(b.name, 'ja'))
      .map((account): AccountRow => {
        const subsMap = bySub.get(account.id) ?? new Map();
        const subs: SubRow[] = subAccounts
          .filter((s) => s.account_id === account.id && (s.active || subsMap.has(s.id)))
          .sort((a, b) => a.display_order - b.display_order)
          .map((s) => ({ subAccountId: s.id, name: s.name, ...(subsMap.get(s.id) ?? { actual: 0, forecast: 0 }) }));
        // 別の科目①の科目②が付いた取引(付け替え漏れ)も、その名前で出す
        for (const [id, v] of subsMap) {
          if (id === null || subs.some((x) => x.subAccountId === id)) continue;
          subs.push({ subAccountId: id, name: subById.get(id)?.name ?? id, ...v });
        }
        const unassigned = subsMap.get(null);
        if (unassigned) subs.push({ subAccountId: null, name: '(科目②未選択)', ...unassigned });
        return { account, amounts: byAccount.get(account.id) ?? zero(), subs };
      });
    const total = zero();
    for (const r of rows) add(total, r.amounts);
    return { key: def.key, label: def.label, cost: def.cost, accounts: rows, total };
  });

  // 利益は月ごとに出して足す(税金等は月ごとに MAX(0, …) のため、期間の合計から出すと v8 と合わない)
  const profits = Object.fromEntries(
    (['gross', 'operating', 'ordinary', 'pretax', 'tax', 'net'] as ProfitKey[]).map((k) => [k, zero()]),
  ) as Record<ProfitKey, Amounts>;
  for (const m of months) {
    const p = profitsOf(monthTotals(m));
    add(profits.gross, p.gross);
    add(profits.operating, p.operating);
    add(profits.ordinary, p.ordinary);
    add(profits.pretax, p.pretax);
    // 見込みは「実績に上乗せされる分」なので、税金等も上乗せ分(実績+見込みの税 − 実績の税)で出す
    const tax: Amounts = {
      budget: taxOf(p.pretax.budget),
      actual: taxOf(p.pretax.actual),
      forecast: taxOf(p.pretax.actual + p.pretax.forecast) - taxOf(p.pretax.actual),
    };
    add(profits.tax, tax);
    add(profits.net, {
      budget: p.pretax.budget - tax.budget,
      actual: p.pretax.actual - tax.actual,
      forecast: p.pretax.forecast - tax.forecast,
    });
  }

  const sales = sections.find((s) => s.key === 'sales')?.total ?? zero();
  return { months, sections, profits, sales, unknownAccountCount };
}

/** 対売上比(%・小数1桁)。売上が0なら null */
export function ratioToSales(value: number, sales: number): number | null {
  if (sales === 0) return null;
  return Math.round((value / sales) * 1000) / 10;
}

// --- 期間(月・四半期・年度累計・年度合計) ---------------------------------------

export type PeriodMode = 'month' | 'quarter' | 'ytd' | 'year';

export const PERIOD_OPTIONS: Array<{ value: PeriodMode; label: string }> = [
  { value: 'month', label: '月' },
  { value: 'quarter', label: '四半期' },
  { value: 'ytd', label: '年度累計' },
  { value: 'year', label: '年度合計' },
];

function monthIndex(m: MonthStr): number {
  const [y, mm] = m.split('-').map(Number) as [number, number];
  return y * 12 + (mm - 1);
}

function monthOfIndex(i: number): MonthStr {
  return `${Math.floor(i / 12)}-${String((i % 12) + 1).padStart(2, '0')}`;
}

/** 年度の最初の月。startMonth=1 なら 1月始まり(v8 の 2027年度 = 2027-01〜12) */
export function fiscalStartOf(month: MonthStr, startMonth: number): MonthStr {
  const [y, m] = month.split('-').map(Number) as [number, number];
  return `${m >= startMonth ? y : y - 1}-${String(startMonth).padStart(2, '0')}`;
}

/** 選んだ月と期間の種類から、集計する月の一覧と見出し */
export function periodMonths(mode: PeriodMode, month: MonthStr, startMonth: number): { months: MonthStr[]; label: string } {
  const start = monthIndex(fiscalStartOf(month, startMonth));
  const cur = monthIndex(month);
  const fy = Number(fiscalStartOf(month, startMonth).slice(0, 4));
  const range = (from: number, to: number) => Array.from({ length: to - from + 1 }, (_, i) => monthOfIndex(from + i));
  const short = (m: MonthStr) => `${Number(m.slice(5, 7))}月`;
  if (mode === 'month') return { months: [month], label: `${month.slice(0, 4)}年${short(month)}` };
  if (mode === 'quarter') {
    const q = Math.floor((cur - start) / 3);
    const months = range(start + q * 3, start + q * 3 + 2);
    return { months, label: `${fy}年度 ${q + 1}Q(${short(months[0]!)}〜${short(months[2]!)})` };
  }
  if (mode === 'ytd') {
    const months = range(start, cur);
    return { months, label: `${fy}年度 累計(${short(months[0]!)}〜${short(month)})` };
  }
  const months = range(start, start + 11);
  return { months, label: `${fy}年度 合計(${short(months[0]!)}〜${short(months[11]!)})` };
}
